import React, { ComponentType, FC } from 'react';
import { theme } from '../../theme';
import { Stack } from '../../components/Stack';

import { RootStack, Label, Hint, Error, BlockTooltip } from './styled';
import { HelpIcon } from '../../tempIcons/HelpIcon';
import { useId } from '../../hooks/useId';

export interface WithLabelsProps {
  id?: string;
  label?: string;
  hint?: string | React.ReactNode;
  error?: string;
  tooltip?: string;
}

export const withLabels =
  <P extends object>(Component: ComponentType<P>): FC<P & WithLabelsProps> =>
  ({ label, hint, error, tooltip, ...otherProps }: WithLabelsProps) => {
    const componentId = useId({ id: otherProps?.id, prefix: Component.name });

    if (!label) return <Component hasError={!!error} {...(otherProps as P)} />;

    return (
      <RootStack spacing="sm" alignX="stretch">
        <div style={{ marginBottom: theme.sizes.xs }}>
          <Stack direction="horizontal" alignY="center" spacing="sm">
            <Label id={`${componentId}-label`} htmlFor={componentId}>
              {label}
            </Label>
            {tooltip && (
              <BlockTooltip text={tooltip}>
                <HelpIcon
                  width={theme.sizes.base}
                  height={theme.sizes.base}
                  color={theme.colors.neutral.ink.light}
                />
              </BlockTooltip>
            )}
          </Stack>
          {hint && <Hint>{hint}</Hint>}
        </div>
        <Component id={componentId} hasError={!!error} {...(otherProps as P)} />
        {error && <Error>{error}</Error>}
      </RootStack>
    );
  };
